import { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  Loader2, 
  Lightbulb, 
  RefreshCw, 
  Save, 
  X, 
  Copy,
  Download,
  Upload,
  Settings
} from 'lucide-react';
import { BlueprintTreeService } from '../../services/blueprintTreeService';
import { BlueprintService } from '../../services/blueprint/BlueprintService';
import { logger } from '../../services/loggingService';
import toast from 'react-hot-toast';
import type { BlueprintTreeNode } from '../../services/blueprintTreeService';

interface DrillDownPromptInterfaceProps {
  selectedNode: BlueprintTreeNode | null;
  nodeData: any;
  promptContext: string;
  suggestions: string[];
  onNodeUpdate: (nodeId: string, updatedData: any) => void;
  onClose: () => void;
}

export function DrillDownPromptInterface({
  selectedNode,
  nodeData,
  promptContext,
  suggestions,
  onNodeUpdate,
  onClose
}: DrillDownPromptInterfaceProps) {
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{
    role: 'user' | 'ai';
    content: string;
    timestamp: string;
  }>>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedNode) {
      setConversationHistory([]);
      setResponse('');
      setPrompt('');
      setEditedData(nodeData);
    }
  }, [selectedNode, nodeData]);

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  const handleSendPrompt = async () => {
    if (!prompt.trim() || !selectedNode) return;

    setLoading(true);
    const userMessage = {
      role: 'user' as const,
      content: prompt,
      timestamp: new Date().toISOString()
    };

    setConversationHistory(prev => [...prev, userMessage]);

    try {
      // Simulate AI response (replace with actual AI service call)
      const aiResponse = await simulateAIResponse(prompt, promptContext, nodeData);
      
      const aiMessage = {
        role: 'ai' as const,
        content: aiResponse,
        timestamp: new Date().toISOString()
      };

      setConversationHistory(prev => [...prev, aiMessage]);
      setResponse(aiResponse);

      // Update node data based on response
      const projectId = nodeData?.project_id || selectedNode?.project_id;
      const updatedData = await processAIResponse(selectedNode.id, prompt, aiResponse, projectId);
      if (updatedData) {
        setEditedData(updatedData);
        onNodeUpdate(selectedNode.id, updatedData);
        if (updatedData.status === 'updated') {
          toast.success('Blueprint updated successfully');
        } else if (updatedData.status === 'failed') {
          toast.error('Failed to update blueprint. Changes saved locally.');
        }
      }

    } catch (error) {
      logger.error('Error processing prompt', { error, nodeId: selectedNode.id, prompt });
      toast.error('Failed to process prompt');
    } finally {
      setLoading(false);
      setPrompt('');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
    textareaRef.current?.focus();
  };

  const handleSaveChanges = async () => {
    if (!selectedNode || !editedData) return;

    try {
      setLoading(true);
      await BlueprintTreeService.updateNodeFromPrompt(
        selectedNode.id,
        'project-id', // This should come from props
        'Manual edit',
        JSON.stringify(editedData)
      );
      
      onNodeUpdate(selectedNode.id, editedData);
      setIsEditing(false);
      toast.success('Changes saved successfully');
    } catch (error) {
      logger.error('Error saving changes', { error, nodeId: selectedNode.id });
      toast.error('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    if (!selectedNode || !editedData) return;

    const dataStr = JSON.stringify(editedData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedNode.label.toLowerCase().replace(/\s+/g, '-')}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully');
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);
        setEditedData(importedData);
        setIsEditing(true);
        toast.success('Data imported successfully');
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  if (!selectedNode) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Lightbulb className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Select a Blueprint Item
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Choose an item from the sidebar to start refining it through conversation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{selectedNode.icon}</span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedNode.label}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {selectedNode.type} • {conversationHistory.length} messages
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              isEditing 
                ? 'btn-primary' 
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {isEditing ? 'Viewing' : 'Edit'}
          </button>
          <button
            onClick={handleExportData}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            title="Export data"
          >
            <Download className="w-4 h-4" />
          </button>
          <label className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer" title="Import data">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              accept=".json"
              onChange={handleImportData}
              className="hidden"
            />
          </label>
          <button
            onClick={onClose}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversation Panel */}
        <div className="flex-1 flex flex-col">
          {/* Conversation History */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {conversationHistory.length === 0 ? (
              <div className="text-center py-8">
                <Lightbulb className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Start a Conversation
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Use natural language to refine and expand this blueprint item
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="badge badge-primary text-sm hover:opacity-80 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              conversationHistory.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-3xl px-4 py-3 rounded-lg ${
                      message.role === 'user'
                        ? 'btn-primary'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-gray-600 dark:text-gray-400">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={conversationEndRef} />
          </div>

          {/* Prompt Input */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendPrompt();
                    }
                  }}
                  placeholder="Describe what you want to refine or add..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={3}
                  disabled={loading}
                />
                <button
                  onClick={handleSendPrompt}
                  disabled={!prompt.trim() || loading}
                  className="absolute right-2 top-2 p-2 text-accent-500 hover:text-accent-600 disabled:text-muted disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data Panel */}
        {isEditing && (
          <div className="w-96 border-l border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Data
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Modify the data directly
              </p>
            </div>
            <div className="flex-1 p-4">
              <textarea
                value={JSON.stringify(editedData, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setEditedData(parsed);
                  } catch (error) {
                    // Invalid JSON, but allow typing
                  }
                }}
                className="w-full h-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Edit JSON data..."
              />
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSaveChanges}
                disabled={loading}
                className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper functions
async function simulateAIResponse(prompt: string, context: string, data: any): Promise<string> {
  try {
    // Import AI services dynamically to avoid circular dependencies
    const { anthropicService } = await import('../../services/anthropicService');
    
    const fullPrompt = `You are helping refine a project blueprint item. 

Context: ${context}
Item: ${data?.name || 'Unknown Item'}
Current Data: ${JSON.stringify(data, null, 2)}

User Request: ${prompt}

Please provide a detailed, actionable response that helps refine and improve this blueprint item. Focus on:
1. Technical implementation details
2. User experience considerations  
3. Best practices and recommendations
4. Potential improvements or enhancements

Be specific and practical in your suggestions.`;

    const response = await anthropicService.generateCompletion({
      prompt: fullPrompt,
      model: 'claude-3-sonnet-20240229',
      temperature: 0.7,
      max_tokens: 1024,
    });

    return response.content;
  } catch (error) {
    logger.error('Error generating AI response', { error, nodeId: data?.id, projectId: data?.project_id });
    
    // Fallback response
    return `I understand you want to refine the ${data?.name || 'item'}. Based on your prompt, here's what I suggest:\n\n1. Enhanced functionality with better error handling\n2. Improved user experience with clearer feedback\n3. Additional validation and security measures\n\nWould you like me to elaborate on any of these points?`;
  }
}

async function processAIResponse(nodeId: string, prompt: string, response: string, projectId?: string): Promise<any> {
  try {
    if (!projectId) {
      logger.warn('Project ID not provided for AI response processing', { nodeId });
      // Return basic structure if projectId is missing
      return {
        id: nodeId,
        lastUpdated: new Date().toISOString(),
        prompt: prompt,
        response: response,
        status: 'updated'
      };
    }

    // Use BlueprintService to update the blueprint node in the database
    const result = await BlueprintService.updateNodeFromPrompt(nodeId, projectId, prompt, response);
    
    if (result.success) {
      logger.info('Blueprint node updated from AI response', { nodeId, projectId });
      return {
        id: nodeId,
        lastUpdated: new Date().toISOString(),
        prompt: prompt,
        response: response,
        status: 'updated',
        updatedData: result.updatedData,
        newChildren: result.newChildren
      };
    } else {
      logger.warn('Failed to update blueprint node from AI response', { nodeId, projectId });
      return {
        id: nodeId,
        lastUpdated: new Date().toISOString(),
        prompt: prompt,
        response: response,
        status: 'failed'
      };
    }
  } catch (error) {
    logger.error('Error processing AI response', { error, nodeId, projectId });
    // Return basic structure on error
    return {
      id: nodeId,
      lastUpdated: new Date().toISOString(),
      prompt: prompt,
      response: response,
      status: 'error'
    };
  }
}
